import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { greeting, formatDate, initials } from "@/lib/format";
import { SurfaceCard } from "@/components/common/Cards";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Mic, ArrowRight, Sparkles, ClipboardCheck, FileText, ChevronRight, Stethoscope,
  UserPlus, ImageIcon, Users, AlertTriangle,
} from "lucide-react";

function PipeStat({ label, value }) {
  return (
    <div className="rounded-xl border border-[#E2E8F0] dark:border-[#223139] bg-white dark:bg-[#0E1A21] p-4">
      <div className="text-2xl font-extrabold text-[#101516] dark:text-white tabular-nums">{String(value).padStart(2, "0")}</div>
      <div className="mt-1 text-[10px] font-bold uppercase tracking-wider text-[#8B9698]">{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [cases, setCases] = useState([]);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/cases").then((r) => setCases(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="space-y-6"><Skeleton className="h-52 w-full rounded-2xl" /><Skeleton className="h-40 w-full rounded-2xl" /></div>;
  const { pipeline = {}, doctor_review = [], today_schedule = [], active_cases = [] } = data;

  return (
    <div className="space-y-7">
      {/* HERO */}
      <SurfaceCard dark className="p-7 sm:p-9">
        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#223139] bg-[#0E1A21] px-3 py-1 text-[11px] font-semibold text-[#3FD98A] mb-4">
              <Sparkles className="h-3.5 w-3.5" /> You consult. CONTAHA structures the rest.
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white">{greeting()}, {user?.name?.split(" ").slice(0, 2).join(" ")}</h1>
            <p className="mt-2 text-[15px] font-semibold text-white/90">Ready for your next consultation?</p>
            <p className="mt-1.5 text-sm text-[#8B9698] max-w-md">Speak naturally. CONTAHA will structure the consultation, clinical findings and treatment workflow for you.</p>
          </div>
          <div className="shrink-0">
            <button onClick={() => navigate("/consultation/new")} data-testid="hero-new-consultation"
              className="group flex items-center gap-3 rounded-2xl bg-[#00A85A] px-7 py-5 text-white shadow-[0_10px_40px_rgba(0,168,90,0.45)] transition-all hover:bg-[#008F4D] hover:scale-[1.02]">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/15"><Mic className="h-6 w-6" /></div>
              <div className="text-left"><div className="text-lg font-extrabold">+ New Consultation</div><div className="text-xs text-white/80">Start speaking →</div></div>
            </button>
          </div>
        </div>
      </SurfaceCard>

      {/* Report pipeline */}
      <div>
        <div className="mb-3 text-[11px] font-bold uppercase tracking-wider text-[#8B9698]">Report Pipeline</div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <PipeStat label="Consultations Today" value={pipeline.consultations_today || 0} />
          <PipeStat label="Reports Generated" value={pipeline.reports_generated || 0} />
          <PipeStat label="Awaiting Review" value={pipeline.reports_review || 0} />
          <PipeStat label="Plans Approved" value={pipeline.plans_approved || 0} />
          <PipeStat label="Decisions Pending" value={pipeline.decisions_pending || 0} />
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          {/* Doctor review required */}
          <SurfaceCard className="p-0">
            <div className="flex items-center gap-2 border-b border-[#E2E8F0] dark:border-[#223139] px-5 py-4">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <h3 className="text-base font-bold text-[#101516] dark:text-white">Doctor Review Required</h3>
              {doctor_review.length > 0 && <StatusBadge status="warning" label={`${doctor_review.length}`} className="ml-1" />}
            </div>
            <div className="divide-y divide-[#E2E8F0]/60 dark:divide-[#223139]/60">
              {doctor_review.length === 0 && <div className="px-5 py-8 text-center text-sm text-[#8B9698]">Nothing awaiting your review. 🎉</div>}
              {doctor_review.map((item, i) => (
                <button key={i} onClick={() => navigate(item.type === "treatment_plan" ? `/treatment-plan/${item.id}` : `/reports/${item.id}`)} data-testid={`review-item-${i}`}
                  className="flex w-full items-center gap-3 px-5 py-3.5 text-left transition-colors hover:bg-[#F8F9F8] dark:hover:bg-[#0E1A21]">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-500 dark:bg-amber-500/15">{item.type === "treatment_plan" ? <ClipboardCheck className="h-4 w-4" /> : <FileText className="h-4 w-4" />}</div>
                  <div className="min-w-0 flex-1"><div className="text-sm font-semibold text-[#101516] dark:text-white truncate">{item.title} {item.version && <span className="text-[10px] text-[#8B9698]">{item.version}</span>}</div><div className="text-xs text-[#8B9698]">{item.subtitle}</div></div>
                  <ChevronRight className="h-4 w-4 text-[#8B9698]" />
                </button>
              ))}
            </div>
          </SurfaceCard>

          {/* Today's consultations */}
          <SurfaceCard className="p-0">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E2E8F0] dark:border-[#223139]">
              <div className="flex items-center gap-2"><Stethoscope className="h-4 w-4 text-[#00A85A]" /><h3 className="text-base font-bold text-[#101516] dark:text-white">Today's Consultations</h3></div>
              <button onClick={() => navigate("/appointments")} className="text-xs font-semibold text-[#00A85A] flex items-center gap-1">View all <ArrowRight className="h-3.5 w-3.5" /></button>
            </div>
            <div className="divide-y divide-[#E2E8F0]/60 dark:divide-[#223139]/60">
              {today_schedule.length === 0 && <div className="px-5 py-8 text-center text-sm text-[#8B9698]">No consultations scheduled today.</div>}
              {today_schedule.map((a) => (
                <div key={a.id} className="flex items-center gap-4 px-5 py-3.5">
                  <div className="w-14 text-center text-sm font-bold tabular-nums text-[#101516] dark:text-white">{a.time}</div>
                  <div className="h-10 w-px bg-[#E2E8F0] dark:bg-[#223139]" />
                  <div className="min-w-0 flex-1"><div className="text-sm font-semibold text-[#101516] dark:text-white">{a.patient_name}</div><div className="text-xs text-[#8B9698] truncate">{a.procedure}</div></div>
                  <StatusBadge status={a.status} />
                </div>
              ))}
            </div>
          </SurfaceCard>

          {/* Recent cases */}
          <SurfaceCard className="p-0">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E2E8F0] dark:border-[#223139]">
              <h3 className="text-base font-bold text-[#101516] dark:text-white">Recent Cases</h3>
              <button onClick={() => navigate("/cases")} className="text-xs font-semibold text-[#00A85A] flex items-center gap-1">View all <ArrowRight className="h-3.5 w-3.5" /></button>
            </div>
            <div className="divide-y divide-[#E2E8F0]/60 dark:divide-[#223139]/60">
              {active_cases.map((c) => (
                <button key={c.id} onClick={() => navigate(`/cases/${c.id}`)} className="flex w-full items-center gap-4 px-5 py-3.5 text-left hover:bg-[#F8F9F8] dark:hover:bg-[#0E1A21]">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#E8F7F0] dark:bg-[#00A85A]/15 text-xs font-bold text-[#008F4D] dark:text-[#3FD98A]">{initials(c.patient_name)}</div>
                  <div className="min-w-0 flex-1"><div className="text-sm font-semibold text-[#101516] dark:text-white truncate">{c.patient_name}</div><div className="text-xs text-[#8B9698] truncate">{c.title}</div></div>
                  <StatusBadge status="in_progress" label={c.stage?.replace(/_/g, " ")} />
                  <ChevronRight className="h-4 w-4 text-[#8B9698]" />
                </button>
              ))}
            </div>
          </SurfaceCard>
        </div>

        {/* Quick actions */}
        <div className="space-y-6">
          <SurfaceCard className="p-5">
            <div className="text-[11px] font-bold uppercase tracking-wider text-[#8B9698] mb-3">Quick Actions</div>
            <div className="space-y-2">
              {[
                { label: "New Consultation", icon: Mic, to: "/consultation/new", primary: true },
                { label: "Existing Patient", icon: Users, to: "/consultation/new" },
                { label: "Upload Imaging", icon: ImageIcon, to: "/consultation/new" },
                { label: "View Reports", icon: FileText, to: "/reports" },
                { label: "View Treatment Plans", icon: ClipboardCheck, to: "/treatment-plans" },
              ].map((q) => (
                <button key={q.label} onClick={() => navigate(q.to)} data-testid={`quick-${q.label.toLowerCase().replace(/ /g, "-")}`}
                  className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm font-semibold transition-colors ${q.primary ? "border-[#00A85A] bg-[#00A85A] text-white hover:bg-[#008F4D]" : "border-[#E2E8F0] dark:border-[#223139] text-[#101516] dark:text-white hover:border-[#00A85A]/40"}`}>
                  <q.icon className="h-4 w-4" />{q.label}
                </button>
              ))}
            </div>
          </SurfaceCard>

          <SurfaceCard dark className="p-5">
            <div className="flex items-center gap-2 mb-2"><Sparkles className="h-4 w-4 text-[#3FD98A]" /><span className="text-sm font-bold text-white">CONTAHA AI</span></div>
            <p className="text-xs text-[#8B9698]">Every clinical step follows: <b className="text-white">AI recommendation → Doctor review → Doctor approval</b>. AI assists, you decide.</p>
            <button onClick={() => navigate("/ai")} className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-lg border border-[#223139] py-2.5 text-xs font-semibold text-white hover:bg-[#0E1A21]">Open CONTAHA AI <ArrowRight className="h-3.5 w-3.5" /></button>
          </SurfaceCard>
        </div>
      </div>
    </div>
  );
}
